import React, { createContext, useCallback, useContext, useMemo, useRef, useState, PropsWithChildren } from 'react';
import api from 'shared/api/api';
import { SESSION_CHANGED } from 'shared/api/session';
import { config } from 'shared/config';
import { useEventListener, useLoad, useMountEffect, useSyncOutside } from 'shared/hooks';
import {
    AdjustSectionsState,
    ConnectionModelUIPreference,
    PropertiesSectionsState,
    ReportUIPreference,
    ReportActiveView,
    RightDrawerTab,
    UIPreferences,
    UIPreferencesResponse,
    DataChatWorkspacePreference,
    DataChatWorkspaceLayout,
    FiltersDrawerPreference,
    WorkspacePosition,
    WorkspaceSize,
} from 'features/preferences/public/model/types';
import { REPORT_LAYOUT_TOKENS } from 'features/report-workspace/public/reportLayout';

const SAVE_DEBOUNCE_MS = 400;

const MIN_VIEWPORT_ZOOM_PERCENT = 50;
const MAX_VIEWPORT_ZOOM_PERCENT = 200;
const DEFAULT_VIEWPORT_ZOOM_PERCENT = 100;
const DEFAULT_REPORT_VIEW: ReportActiveView = 'page';
const MIN_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT = 50;
const MAX_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT = 180;
const DEFAULT_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT = 100;
const DEFAULT_FILTERS_DRAWER_WIDTH = REPORT_LAYOUT_TOKENS.filtersDrawer.defaultWidth;
const MIN_FILTERS_DRAWER_WIDTH = REPORT_LAYOUT_TOKENS.filtersDrawer.minWidth;
const MAX_FILTERS_DRAWER_WIDTH = REPORT_LAYOUT_TOKENS.filtersDrawer.maxWidth;

const clampZoomPercent = (value: number): number => {
    const rounded = Math.round(value);
    if (Number.isNaN(rounded)) {
        return DEFAULT_VIEWPORT_ZOOM_PERCENT;
    }
    return Math.min(MAX_VIEWPORT_ZOOM_PERCENT, Math.max(MIN_VIEWPORT_ZOOM_PERCENT, rounded));
};

const clampConnectionModelRelationshipZoomPercent = (value: number): number => {
    const rounded = Math.round(value);
    if (Number.isNaN(rounded)) {
        return DEFAULT_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT;
    }
    return Math.min(
        MAX_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT,
        Math.max(MIN_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT, rounded),
    );
};

const clampFiltersDrawerWidth = (value: number): number => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return DEFAULT_FILTERS_DRAWER_WIDTH;
    }
    return Math.min(MAX_FILTERS_DRAWER_WIDTH, Math.max(MIN_FILTERS_DRAWER_WIDTH, numeric));
};

const sanitizeStringList = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }
    const seen = new Set<string>();
    const next: string[] = [];
    value.forEach((item) => {
        const text = String(item ?? '').trim();
        const key = text.toLowerCase();
        if (!text || seen.has(key)) {
            return;
        }
        seen.add(key);
        next.push(text);
    });
    return next;
};

const areEquivalentStringLists = (left: unknown, right: unknown): boolean => {
    const leftValues = sanitizeStringList(left).map((entry) => entry.toLowerCase());
    const rightValues = sanitizeStringList(right).map((entry) => entry.toLowerCase());
    if (leftValues.length !== rightValues.length) {
        return false;
    }
    const rightSet = new Set(rightValues);
    return leftValues.every((entry) => rightSet.has(entry));
};

const normalizeReportView = (view?: string | null): ReportActiveView => {
    if (!view) {
        return DEFAULT_REPORT_VIEW;
    }
    const normalised = view.toLowerCase();
    if (normalised === 'data' || normalised === 'playground') {
        return normalised as ReportActiveView;
    }
    return DEFAULT_REPORT_VIEW;
};

const createDefaultDataChatWorkspacePreference = (): DataChatWorkspacePreference => ({
    visible: true,
    position: null,
    size: null,
    activeChatId: null,
});

const createDefaultFiltersDrawerPreference = (): FiltersDrawerPreference => ({
    collapsed: false,
    width: DEFAULT_FILTERS_DRAWER_WIDTH,
});

const createDefaultConnectionModelPreference = (): ConnectionModelUIPreference => ({
    assistantCollapsed: false,
    relationshipEditorCollapsed: false,
    relationshipZoomPercent: DEFAULT_CONNECTION_MODEL_RELATIONSHIP_ZOOM_PERCENT,
});

const createDefaultReportPreference = (): ReportUIPreference => ({
    rightDrawerTab: 'data',
    sections: {
        properties: {
            modifyVisual: true,
            visualProperties: true,
            appearance: true,
        },
        adjust: {
            fields: true,
            chartSpec: false,
            visualProperties: true,
            appearance: true,
        },
        data: {
            expandedTables: [],
        },
    },
    dataChats: {},
    dataChatWorkspace: createDefaultDataChatWorkspacePreference(),
    filtersDrawer: createDefaultFiltersDrawerPreference(),
    connectionModel: createDefaultConnectionModelPreference(),
    viewportZoomPercent: DEFAULT_VIEWPORT_ZOOM_PERCENT,
    activeView: DEFAULT_REPORT_VIEW,
});

const normalizeDataChatWorkspacePreference = (input?: DataChatWorkspacePreference | null): DataChatWorkspacePreference => {
    if (!input) {
        return createDefaultDataChatWorkspacePreference();
    }

    const position = input.position;
    const size = input.size;

    const normalisedPosition = position && typeof position.x === 'number' && typeof position.y === 'number'
        ? { x: position.x, y: position.y }
        : null;

    const normalisedSize = size && typeof size.width === 'number' && typeof size.height === 'number'
        ? { width: size.width, height: size.height }
        : null;

    const rawActiveChatId = (input as { activeChatId?: unknown }).activeChatId;
    let normalisedActiveChatId: string | null = null;
    if (typeof rawActiveChatId === 'string') {
        const trimmed = rawActiveChatId.trim();
        normalisedActiveChatId = trimmed ? trimmed : null;
    } else if (typeof rawActiveChatId === 'number' && Number.isFinite(rawActiveChatId)) {
        normalisedActiveChatId = String(rawActiveChatId);
    }

    return {
        visible: typeof input.visible === 'boolean' ? input.visible : false,
        position: normalisedPosition,
        size: normalisedSize,
        activeChatId: normalisedActiveChatId,
    };
};

const normalizeConnectionModelPreference = (input?: ConnectionModelUIPreference | null): ConnectionModelUIPreference => {
    if (!input) {
        return createDefaultConnectionModelPreference();
    }
    return {
        assistantCollapsed: typeof input.assistantCollapsed === 'boolean'
            ? input.assistantCollapsed
            : false,
        relationshipEditorCollapsed: typeof input.relationshipEditorCollapsed === 'boolean'
            ? input.relationshipEditorCollapsed
            : false,
        relationshipZoomPercent: clampConnectionModelRelationshipZoomPercent(input.relationshipZoomPercent),
    };
};

const normalizeFiltersDrawerPreference = (input?: FiltersDrawerPreference | null): FiltersDrawerPreference => {
    if (!input) {
        return createDefaultFiltersDrawerPreference();
    }
    return {
        collapsed: typeof input.collapsed === 'boolean' ? input.collapsed : false,
        width: clampFiltersDrawerWidth(input.width),
    };
};

const normalizeReportPreference = (input?: ReportUIPreference | null): ReportUIPreference => {
    if (!input) {
        return createDefaultReportPreference();
    }

    const normalizedZoomPercent = typeof input.viewportZoomPercent === 'number'
        ? clampZoomPercent(input.viewportZoomPercent)
        : DEFAULT_VIEWPORT_ZOOM_PERCENT;
    const normalizedView = normalizeReportView((input as { activeView?: string | null }).activeView);

    return {
        rightDrawerTab: input.rightDrawerTab ?? 'data',
        sections: {
            properties: {
                modifyVisual: input.sections?.properties?.modifyVisual ?? true,
                visualProperties: input.sections?.properties?.visualProperties ?? true,
                appearance: input.sections?.properties?.appearance ?? true,
            },
            adjust: {
                fields: input.sections?.adjust?.fields ?? true,
                chartSpec: input.sections?.adjust?.chartSpec ?? false,
                visualProperties: input.sections?.adjust?.visualProperties
                    ?? input.sections?.properties?.visualProperties
                    ?? true,
                appearance: input.sections?.adjust?.appearance
                    ?? input.sections?.properties?.appearance
                    ?? true,
            },
            data: {
                expandedTables: input.sections?.data?.expandedTables
                    ? [...input.sections.data.expandedTables]
                    : [],
            },
        },
        dataChats: input.dataChats ? { ...input.dataChats } : {},
        dataChatWorkspace: normalizeDataChatWorkspacePreference(input.dataChatWorkspace),
        filtersDrawer: normalizeFiltersDrawerPreference(input.filtersDrawer),
        connectionModel: normalizeConnectionModelPreference(input.connectionModel),
        viewportZoomPercent: normalizedZoomPercent,
        activeView: normalizedView,
    };
};

const normalizePreferences = (input?: UIPreferences | null): UIPreferences => {
    const base: UIPreferences = {
        leftDrawerMinimized: input?.leftDrawerMinimized ?? true,
        rightDrawerMinimized: input?.rightDrawerMinimized ?? false,
        rightDrawerWidth: typeof input?.rightDrawerWidth === 'number'
            ? input.rightDrawerWidth
            : REPORT_LAYOUT_TOKENS.rightDrawer.defaultWidth,
        reports: {},
    };

    const reports = input?.reports ?? {};
    for (const [reportId, prefs] of Object.entries(reports)) {
        base.reports[reportId] = normalizeReportPreference(prefs);
    }

    return base;
};

interface UIPreferencesContextValue {
    userId?: string;
    loading: boolean;
    hasLoadedPreferences: boolean;
    isSaving: boolean;
    error?: string;
    preferences: UIPreferences;
    getReportPreferences: (reportId: string) => ReportUIPreference;
    ensureReportPreferences: (reportId: string) => void;
    setViewportZoomPercent: (reportId: string, zoomPercent: number) => void;
    setActiveReportView: (reportId: string, view: ReportActiveView) => void;
    setLeftDrawerMinimized: (value: boolean) => void;
    setRightDrawerMinimized: (value: boolean) => void;
    setRightDrawerWidth: (value: number) => void;
    setRightDrawerTab: (reportId: string, tab: RightDrawerTab) => void;
    setPropertiesSection: (reportId: string, section: keyof PropertiesSectionsState, expanded: boolean) => void;
    setAdjustSection: (reportId: string, section: keyof AdjustSectionsState, expanded: boolean) => void;
    setDataExpandedTables: (reportId: string, tables: string[]) => void;
    setDataChatCollapsed: (reportId: string, dataChatId: string | number, collapsed: boolean) => void;
    setDataChatWorkspaceVisibility: (reportId: string, visible: boolean) => void;
    setDataChatWorkspaceLayout: (reportId: string, layout: DataChatWorkspaceLayout) => void;
    setDataChatWorkspaceActiveChat: (reportId: string, dataChatId: string | number | null) => void;
    setFiltersDrawerCollapsed: (reportId: string, collapsed: boolean) => void;
    setFiltersDrawerWidth: (reportId: string, width: number) => void;
    setConnectionModelAssistantCollapsed: (reportId: string, collapsed: boolean) => void;
    setConnectionModelRelationshipEditorCollapsed: (reportId: string, collapsed: boolean) => void;
    setConnectionModelRelationshipZoomPercent: (reportId: string, zoomPercent: number) => void;
    refresh: () => Promise<void>;
}

const UIPreferencesContext = createContext<UIPreferencesContextValue | undefined>(undefined);

export const UIPreferencesProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [userId, setUserId] = useState<string | undefined>(undefined);
    const [preferences, setPreferences] = useState<UIPreferences>(() => normalizePreferences());
    const [loading, setLoading] = useState(true);
    const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSaveRef = useRef<UIPreferences | null>(null);

    const queueSave = useCallback((next: UIPreferences) => {
        pendingSaveRef.current = next;

        if (!userId || !hasLoadedPreferences) {
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            if (!pendingSaveRef.current || !userId) {
                return;
            }

            try {
                setIsSaving(true);
                await api.put(`${config.API_URL}/api/ui/preferences`, {
                    userId,
                    preferences: pendingSaveRef.current,
                });
                setError(undefined);
            } catch (err) {
                setError('Failed to save UI preferences');
            } finally {
                setIsSaving(false);
            }
        }, SAVE_DEBOUNCE_MS);
    }, [hasLoadedPreferences, userId]);

    const updatePreferences = useCallback((updater: (prev: UIPreferences) => UIPreferences) => {
        setPreferences((prev) => {
            const next = updater(prev);
            queueSave(next);
            return next;
        });
    }, [queueSave]);

    const loadFromStorage = useCallback(() => {
        try {
            const stored = localStorage.getItem('user');
            if (!stored) {
                setUserId(undefined);
                return;
            }
            const parsed = JSON.parse(stored);
            if (parsed?.userId) {
                setUserId(parsed.userId);
            } else {
                setUserId(undefined);
            }
        } catch (err) {
            setUserId(undefined);
        }
    }, []);

    const fetchPreferences = useCallback(async () => {
        if (!userId) {
            setPreferences(normalizePreferences());
            setHasLoadedPreferences(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await api.get<UIPreferencesResponse>(`${config.API_URL}/api/ui/preferences`, {
                params: { userId },
            });

            const normalized = normalizePreferences(response.data?.preferences);
            setPreferences(normalized);
            setHasLoadedPreferences(true);
            setError(undefined);
        } catch (err: any) {
            setHasLoadedPreferences(false);
            setError('Failed to load UI preferences');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Preferences saved in this browser, and again when another tab changes them.
    useLoad(() => loadFromStorage(), [loadFromStorage]);
    useEventListener('storage', () => loadFromStorage());
    // A browser with a cookie but no stored user is looked up after this provider mounted (SessionBootstrap): read the user then.
    useEventListener(SESSION_CHANGED, () => loadFromStorage());

    useLoad(() => {
        fetchPreferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, fetchPreferences]);

    useSyncOutside(() => {
        if (userId && hasLoadedPreferences && pendingSaveRef.current) {
            queueSave(pendingSaveRef.current);
        }
    }, [hasLoadedPreferences, queueSave, userId]);

    useMountEffect(() => () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
    });

    const getReportPreferences = useCallback(
        (reportId: string): ReportUIPreference => {
            return preferences.reports[reportId]
                ? normalizeReportPreference(preferences.reports[reportId])
                : createDefaultReportPreference();
        },
        [preferences.reports],
    );

    const ensureReportPreferences = useCallback((reportId: string) => {
        setPreferences((prev) => {
            const existing = prev.reports[reportId];
            if (existing) {
                const normalizedZoom = clampZoomPercent(typeof existing.viewportZoomPercent === 'number'
                    ? existing.viewportZoomPercent
                    : DEFAULT_VIEWPORT_ZOOM_PERCENT);
                const normalizedView = normalizeReportView(existing.activeView as string | null);
                const normalizedFiltersDrawer = normalizeFiltersDrawerPreference(existing.filtersDrawer);
                const needsUpdate = existing.viewportZoomPercent !== normalizedZoom
                    || existing.activeView !== normalizedView
                    || existing.filtersDrawer?.collapsed !== normalizedFiltersDrawer.collapsed
                    || existing.filtersDrawer?.width !== normalizedFiltersDrawer.width;
                if (!needsUpdate) {
                    return prev;
                }
                const next: UIPreferences = {
                    ...prev,
                    reports: {
                        ...prev.reports,
                        [reportId]: {
                            ...existing,
                            viewportZoomPercent: normalizedZoom,
                            activeView: normalizedView,
                            filtersDrawer: normalizedFiltersDrawer,
                        },
                    },
                };
                queueSave(next);
                return next;
            }
            const nextReportPrefs = createDefaultReportPreference();
            const next: UIPreferences = {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: nextReportPrefs,
                },
            };
            queueSave(next);
            return next;
        });
    }, [queueSave]);

    const setViewportZoomPercent = useCallback((reportId: string, zoomPercent: number) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const nextZoom = clampZoomPercent(zoomPercent);
            if (current.viewportZoomPercent === nextZoom) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        viewportZoomPercent: nextZoom,
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setActiveReportView = useCallback((reportId: string, view: ReportActiveView) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const normalisedView = normalizeReportView(view);
            if (current.activeView === normalisedView) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        activeView: normalisedView,
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setLeftDrawerMinimized = useCallback((value: boolean) => {
        updatePreferences((prev) => ({
            ...prev,
            leftDrawerMinimized: value,
        }));
    }, [updatePreferences]);

    const setRightDrawerMinimized = useCallback((value: boolean) => {
        updatePreferences((prev) => ({
            ...prev,
            rightDrawerMinimized: value,
        }));
    }, [updatePreferences]);

    const setRightDrawerWidth = useCallback((value: number) => {
        updatePreferences((prev) => ({
            ...prev,
            rightDrawerWidth: value,
        }));
    }, [updatePreferences]);

    const setRightDrawerTab = useCallback((reportId: string, tab: RightDrawerTab) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        rightDrawerTab: tab,
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setPropertiesSection = useCallback((reportId: string, section: keyof PropertiesSectionsState, expanded: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        sections: {
                            ...current.sections,
                            properties: {
                                ...current.sections.properties,
                                [section]: expanded,
                            },
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setAdjustSection = useCallback((reportId: string, section: keyof AdjustSectionsState, expanded: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        sections: {
                            ...current.sections,
                            adjust: {
                                ...current.sections.adjust,
                                [section]: expanded,
                            },
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setDataExpandedTables = useCallback((reportId: string, tables: string[]) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const nextExpandedTables = sanitizeStringList(tables);
            if (areEquivalentStringLists(current.sections.data.expandedTables, nextExpandedTables)) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        sections: {
                            ...current.sections,
                            data: {
                                expandedTables: nextExpandedTables,
                            },
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setDataChatCollapsed = useCallback((reportId: string, dataChatId: string | number, collapsed: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        dataChats: {
                            ...current.dataChats,
                            [String(dataChatId)]: collapsed,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setDataChatWorkspaceVisibility = useCallback((reportId: string, visible: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        dataChatWorkspace: {
                            ...current.dataChatWorkspace,
                            visible,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setDataChatWorkspaceLayout = useCallback((reportId: string, layout: DataChatWorkspaceLayout) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const nextPosition: WorkspacePosition = {
                x: Math.round(layout.position.x),
                y: Math.round(layout.position.y),
            };
            const nextSize: WorkspaceSize = {
                width: Math.max(0, Math.round(layout.size.width)),
                height: Math.max(0, Math.round(layout.size.height)),
            };
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        dataChatWorkspace: {
                            ...current.dataChatWorkspace,
                            position: nextPosition,
                            size: nextSize,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setDataChatWorkspaceActiveChat = useCallback((reportId: string, dataChatId: string | number | null) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const rawValue = dataChatId === null || dataChatId === undefined
                ? null
                : String(dataChatId).trim();
            const nextActiveChatId = rawValue && rawValue.length > 0 ? rawValue : null;
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        dataChatWorkspace: {
                            ...current.dataChatWorkspace,
                            activeChatId: nextActiveChatId,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setFiltersDrawerCollapsed = useCallback((reportId: string, collapsed: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            if (current.filtersDrawer?.collapsed === collapsed) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        filtersDrawer: {
                            ...normalizeFiltersDrawerPreference(current.filtersDrawer),
                            collapsed,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setFiltersDrawerWidth = useCallback((reportId: string, width: number) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const nextWidth = clampFiltersDrawerWidth(width);
            if (current.filtersDrawer?.width === nextWidth) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        filtersDrawer: {
                            ...normalizeFiltersDrawerPreference(current.filtersDrawer),
                            width: nextWidth,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setConnectionModelAssistantCollapsed = useCallback((reportId: string, collapsed: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            if (current.connectionModel?.assistantCollapsed === collapsed) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        connectionModel: {
                            ...current.connectionModel,
                            assistantCollapsed: collapsed,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setConnectionModelRelationshipEditorCollapsed = useCallback((reportId: string, collapsed: boolean) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            if (current.connectionModel?.relationshipEditorCollapsed === collapsed) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        connectionModel: {
                            ...current.connectionModel,
                            relationshipEditorCollapsed: collapsed,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const setConnectionModelRelationshipZoomPercent = useCallback((reportId: string, zoomPercent: number) => {
        updatePreferences((prev) => {
            const current = prev.reports[reportId] ?? createDefaultReportPreference();
            const nextZoomPercent = clampConnectionModelRelationshipZoomPercent(zoomPercent);
            if (current.connectionModel?.relationshipZoomPercent === nextZoomPercent) {
                return prev;
            }
            return {
                ...prev,
                reports: {
                    ...prev.reports,
                    [reportId]: {
                        ...current,
                        connectionModel: {
                            ...current.connectionModel,
                            relationshipZoomPercent: nextZoomPercent,
                        },
                    },
                },
            };
        });
    }, [updatePreferences]);

    const value = useMemo<UIPreferencesContextValue>(() => ({
        userId,
        loading,
        hasLoadedPreferences,
        isSaving,
        error,
        preferences,
        getReportPreferences,
        ensureReportPreferences,
        setViewportZoomPercent,
        setActiveReportView,
        setLeftDrawerMinimized,
        setRightDrawerMinimized,
        setRightDrawerWidth,
        setRightDrawerTab,
        setPropertiesSection,
        setAdjustSection,
        setDataExpandedTables,
        setDataChatCollapsed,
        setDataChatWorkspaceVisibility,
        setDataChatWorkspaceLayout,
        setDataChatWorkspaceActiveChat,
        setFiltersDrawerCollapsed,
        setFiltersDrawerWidth,
        setConnectionModelAssistantCollapsed,
        setConnectionModelRelationshipEditorCollapsed,
        setConnectionModelRelationshipZoomPercent,
        refresh: fetchPreferences,
    }), [
        userId,
        loading,
        hasLoadedPreferences,
        isSaving,
        error,
        preferences,
        getReportPreferences,
        ensureReportPreferences,
        setViewportZoomPercent,
        setActiveReportView,
        setLeftDrawerMinimized,
        setRightDrawerMinimized,
        setRightDrawerWidth,
        setRightDrawerTab,
        setPropertiesSection,
        setAdjustSection,
        setDataExpandedTables,
        setDataChatCollapsed,
        setDataChatWorkspaceVisibility,
        setDataChatWorkspaceLayout,
        setDataChatWorkspaceActiveChat,
        setFiltersDrawerCollapsed,
        setFiltersDrawerWidth,
        setConnectionModelAssistantCollapsed,
        setConnectionModelRelationshipEditorCollapsed,
        setConnectionModelRelationshipZoomPercent,
        fetchPreferences,
    ]);

    return (
        <UIPreferencesContext.Provider value={value}>
            {children}
        </UIPreferencesContext.Provider>
    );
};

export const useUIPreferencesContext = (): UIPreferencesContextValue => {
    const context = useContext(UIPreferencesContext);
    if (!context) {
        throw new Error('useUIPreferencesContext must be used within a UIPreferencesProvider');
    }
    return context;
};

export const useUIPreferences = (reportId?: string) => {
    const context = useUIPreferencesContext();
    const reportPreferences = reportId
        ? context.getReportPreferences(reportId)
        : undefined;

    return {
        ...context,
        reportPreferences,
    };
};
